import EmojiPicker from "emoji-picker-react";
import { useState } from "react";
import { KeyboardEvent } from "react";
import { RyeIcon } from "./RyeIcon";
import { RyeTooltip } from "./RyeTooltip";
import { buildClasses } from "../utils/buildClasses";
import "./RyeEmojiPicker.css";
import { keyCodes } from "../utils/KeyCodes";
import {
  useFloating,
  offset,
  flip,
  shift,
  useInteractions,
  useDismiss,
  FloatingPortal,
  autoUpdate,
} from "@floating-ui/react";

export function RyeEmojiPicker({
  initialEmoji = "",
  save,
  height = "md",
  groupHover = false, // add group/ryeemojipicker to the parent
}: {
  initialEmoji?: string;
  save: (newEmoji: string) => void;
  height?: "sm" | "md" | "lg";
  groupHover?: boolean;
}): JSX.Element {
  const [selectedEmoji, setSelectedEmoji] = useState<string>(initialEmoji);
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);

  const { refs, floatingStyles, context } = useFloating({
    open: showEmojiPicker,
    onOpenChange: setShowEmojiPicker,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift()],
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  function handleKeyUp(e: KeyboardEvent<HTMLInputElement>): void {
    if (showEmojiPicker && e.code === keyCodes.ESC) {
      e.stopPropagation();
      setShowEmojiPicker(false);
    }
  }

  return (
    <div
      ref={refs.setReference}
      className={buildClasses(
        {
          if: showEmojiPicker,
          then: buildClasses(
            "border-purple",
            "shadow-glow",
            "shadow-purple-100",
          ),
          else: buildClasses(
            "border-transparent",
            "hover:border-on-surface-200",
          ),
        },
        {
          switch: height,
          cases: new Map([
            ["sm", "h-7"],
            ["md", "h-9"],
            ["lg", "h-11"],
          ]),
        },
        {
          if: groupHover && !showEmojiPicker,
          then: buildClasses(
            "group-hover/ryeeditabletext:border",
            "group-hover/ryeeditabletext:border-on-surface-200",
          ),
        },
        "flex",
        "items-center",
        "cursor-pointer",
        "px-2",
        "rounded-md",
        "border",
      )}
      onKeyUp={handleKeyUp}
    >
      <RyeTooltip text="Change emoji">
        <div
          onClick={() => {
            setShowEmojiPicker(!showEmojiPicker);
          }}
        >
          {selectedEmoji !== "" ? (
            selectedEmoji
          ) : (
            <RyeIcon name="add_reaction" size={"sm"}></RyeIcon>
          )}
        </div>
      </RyeTooltip>
      {showEmojiPicker ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={buildClasses("z-[2000000000]")}
          >
            <EmojiPicker
              onEmojiClick={(emoji) => {
                setSelectedEmoji(emoji.emoji);
                setShowEmojiPicker(false);
                save(emoji.emoji);
              }}
              height={350}
              width={350}
              previewConfig={{ showPreview: false }}
            ></EmojiPicker>
          </div>
        </FloatingPortal>
      ) : null}
    </div>
  );
}
